/**
 * Notes store.
 *
 * Owns the active workspace's note/folder lists for `NotesPage`. Follows
 * `knowledgeStore.ts`'s `loaded`/`init`/`refresh`/`guarded` recipe. Full note
 * bodies aren't held in state — `NotesPage` fetches a note's content on
 * selection via `notesRepository.get`, keeping the summary list light.
 */
import { create } from "zustand";
import type { Note, NoteFolder, NoteSummary } from "@/types";
import { notesRepository } from "@/services/storage/notesRepository";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("notes-store");

interface NotesState {
  notes: NoteSummary[];
  folders: NoteFolder[];
  loaded: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  create: (title: string, folderId?: string | null) => Promise<Note | null>;
  save: (id: string, patch: { title?: string; content?: string; tags?: string[] }) => Promise<void>;
  setPinned: (id: string, pinned: boolean) => Promise<void>;
  setFolder: (id: string, folderId: string | null) => Promise<void>;
  remove: (id: string) => Promise<void>;
  createFolder: (name: string, parentId?: string | null) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
}

async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

export const useNotesStore = create<NotesState>()((set, get) => ({
  notes: [],
  folders: [],
  loaded: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
  },

  refresh: async () => {
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    try {
      const [notes, folders] = await Promise.all([
        notesRepository.listSummaries(workspaceId),
        notesRepository.listFolders(workspaceId),
      ]);
      set({ notes, folders, loaded: true });
    } catch (err) {
      log.error("Failed to load notes", err);
      set({ loaded: true });
    }
  },

  create: async (title, folderId = null) => {
    const trimmed = title.trim() || "Untitled note";
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    let created: Note | null = null;
    await guarded(async () => {
      created = await notesRepository.create({ workspaceId, title: trimmed, folderId });
      await get().refresh();
    }, "Couldn't create note");
    return created;
  },

  save: async (id, patch) => {
    await guarded(async () => {
      await notesRepository.save(id, patch);
      await get().refresh();
    }, "Couldn't save note");
  },

  setPinned: async (id, pinned) => {
    await guarded(async () => {
      await notesRepository.setPinned(id, pinned);
      await get().refresh();
    }, "Couldn't update note");
  },

  setFolder: async (id, folderId) => {
    await guarded(async () => {
      await notesRepository.setFolder(id, folderId);
      await get().refresh();
    }, "Couldn't move note");
  },

  remove: async (id) => {
    set((s) => ({ notes: s.notes.filter((n) => n.id !== id) }));
    await guarded(() => notesRepository.delete(id), "Couldn't delete note");
  },

  createFolder: async (name, parentId = null) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    await guarded(async () => {
      await notesRepository.createFolder(workspaceId, trimmed, parentId);
      await get().refresh();
    }, "Couldn't create folder");
  },

  deleteFolder: async (id) => {
    await guarded(async () => {
      await notesRepository.deleteFolder(id);
      await get().refresh();
    }, "Couldn't delete folder");
  },
}));
